/**
 * EditBudgetLimitModal Component
 * Modal form for creating/editing budget limits (plafonds).
 * A budget limit caps spending on a transaction category OR a transaction tag.
 */

import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { formatCurrency } from '../../utils/formatters';
import { useBudgetStore } from '../../store/budgetStore';
import { budgetApi } from '../../api/endpoints/budget';
import type { BudgetLimit } from '../../api/types';
import logger from '../../utils/logger';

interface EditBudgetLimitModalProps {
  visible: boolean;
  onClose: () => void;
  budgetLimit?: BudgetLimit | null;
}

const EXPENSE_CATEGORIES = [
  { key: 'housing', label: 'Logement' },
  { key: 'food', label: 'Alimentation' },
  { key: 'transport', label: 'Transport' },
  { key: 'insurance', label: 'Assurance' },
  { key: 'subscriptions', label: 'Abonnements' },
  { key: 'leisure', label: 'Loisirs' },
  { key: 'health', label: 'Santé' },
  { key: 'taxes', label: 'Impôts' },
  { key: 'education', label: 'Éducation' },
  { key: 'family_assistance', label: 'Aide familiale' },
  { key: 'children_expenses', label: 'Enfants' },
  { key: 'shopping', label: 'Shopping' },
  { key: 'savings', label: 'Épargne' },
  { key: 'other', label: 'Autre' },
];

type Kind = 'category' | 'tag';

export default function EditBudgetLimitModal({
  visible,
  onClose,
  budgetLimit,
}: EditBudgetLimitModalProps) {
  const { addBudgetLimit, updateBudgetLimit } = useBudgetStore();

  const isEditing = !!budgetLimit;

  const [kind, setKind] = useState<Kind>('category');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [availableTags, setAvailableTags] = useState<Array<{ name: string; color: string | null }>>([]);

  // Load tags when opening in tag mode
  useEffect(() => {
    if (visible && kind === 'tag') {
      budgetApi
        .getUserTags()
        .then((tags) => setAvailableTags(tags))
        .catch((err) => logger.error('[EditBudgetLimit] Tags load error:', err));
    }
  }, [visible, kind]);

  useEffect(() => {
    if (budgetLimit) {
      if (budgetLimit.tag) {
        setKind('tag');
        setSelectedTag(budgetLimit.tag);
        setTagInput(budgetLimit.tag);
        setSelectedCategory('');
      } else {
        setKind('category');
        setSelectedCategory(budgetLimit.category || '');
        setSelectedTag('');
        setTagInput('');
      }
      setMonthlyLimit(budgetLimit.monthly_limit.toString());
    } else {
      setKind('category');
      setSelectedCategory('');
      setSelectedTag('');
      setTagInput('');
      setMonthlyLimit('');
    }
    setShowCategoryPicker(false);
    setShowTagPicker(false);
  }, [budgetLimit, visible]);

  const handleClose = () => {
    setSelectedCategory('');
    setSelectedTag('');
    setTagInput('');
    setMonthlyLimit('');
    setShowCategoryPicker(false);
    setShowTagPicker(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (kind === 'category' && !selectedCategory) {
      Alert.alert('Erreur', 'Veuillez sélectionner une catégorie');
      return;
    }
    const tagValue = (selectedTag || tagInput).trim();
    if (kind === 'tag' && !tagValue) {
      Alert.alert('Erreur', 'Veuillez sélectionner ou saisir un tag');
      return;
    }

    const parsedLimit = parseFloat(monthlyLimit);
    if (isNaN(parsedLimit) || parsedLimit <= 0) {
      Alert.alert('Erreur', 'Veuillez entrer un budget mensuel valide');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditing && budgetLimit) {
        await updateBudgetLimit(budgetLimit.id, { monthly_limit: parsedLimit });
      } else if (kind === 'category') {
        const categoryLabel =
          EXPENSE_CATEGORIES.find((c) => c.key === selectedCategory)?.label || selectedCategory;
        await addBudgetLimit({
          category: selectedCategory,
          monthly_limit: parsedLimit,
          name: categoryLabel,
        });
      } else {
        await addBudgetLimit({
          tag: tagValue,
          monthly_limit: parsedLimit,
          name: tagValue,
        });
      }
      handleClose();
    } catch (err: any) {
      logger.error('[EditBudgetLimit] Error:', err);
      const message = err.response?.data?.error || 'Erreur lors de la sauvegarde';
      Alert.alert('Erreur', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCategoryLabel =
    EXPENSE_CATEGORIES.find((c) => c.key === selectedCategory)?.label || '';

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.content}>
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>
                {isEditing ? 'Modifier le plafond' : 'Nouveau plafond'}
              </Text>
              <TouchableOpacity onPress={handleClose}>
                <Icon name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Current spent (only when editing) */}
            {isEditing && budgetLimit?.spent_amount !== undefined && (
              <View style={styles.spentInfo}>
                <Icon name="information-outline" size={18} color={colors.primary} />
                <Text style={styles.spentInfoText}>
                  Déjà dépensé ce mois : {formatCurrency(budgetLimit.spent_amount)}
                </Text>
              </View>
            )}

            {/* Kind toggle (only when creating) */}
            {!isEditing && (
              <>
                <Text style={styles.label}>Type de plafond</Text>
                <View style={styles.kindToggle}>
                  <TouchableOpacity
                    style={[styles.kindOption, kind === 'category' && styles.kindOptionActive]}
                    onPress={() => setKind('category')}
                  >
                    <Icon
                      name="folder"
                      size={16}
                      color={kind === 'category' ? '#FFFFFF' : colors.textSecondary}
                    />
                    <Text
                      style={[
                        styles.kindOptionText,
                        kind === 'category' && styles.kindOptionTextActive,
                      ]}
                    >
                      Catégorie
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.kindOption, kind === 'tag' && styles.kindOptionActive]}
                    onPress={() => setKind('tag')}
                  >
                    <Icon
                      name="tag"
                      size={16}
                      color={kind === 'tag' ? '#FFFFFF' : colors.textSecondary}
                    />
                    <Text
                      style={[
                        styles.kindOptionText,
                        kind === 'tag' && styles.kindOptionTextActive,
                      ]}
                    >
                      Tag
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* Category picker */}
            {kind === 'category' && (
              <>
                <Text style={styles.label}>Catégorie</Text>
                {isEditing ? (
                  <View style={styles.disabledInput}>
                    <Text style={styles.disabledInputText}>{selectedCategoryLabel}</Text>
                  </View>
                ) : (
                  <>
                    <TouchableOpacity
                      style={styles.pickerButton}
                      onPress={() => setShowCategoryPicker(!showCategoryPicker)}
                    >
                      <Text
                        style={[
                          styles.pickerButtonText,
                          !selectedCategory && styles.pickerButtonPlaceholder,
                        ]}
                      >
                        {selectedCategoryLabel || 'Sélectionner une catégorie'}
                      </Text>
                      <Icon
                        name={showCategoryPicker ? 'chevron-up' : 'chevron-down'}
                        size={20}
                        color={colors.textSecondary}
                      />
                    </TouchableOpacity>

                    {showCategoryPicker && (
                      <View style={styles.dropdownList}>
                        <ScrollView nestedScrollEnabled showsVerticalScrollIndicator={true}>
                          {EXPENSE_CATEGORIES.map((cat) => (
                            <TouchableOpacity
                              key={cat.key}
                              style={[
                                styles.dropdownItem,
                                selectedCategory === cat.key && styles.dropdownItemSelected,
                              ]}
                              onPress={() => {
                                setSelectedCategory(cat.key);
                                setShowCategoryPicker(false);
                              }}
                            >
                              <Text
                                style={[
                                  styles.dropdownItemText,
                                  selectedCategory === cat.key && styles.dropdownItemTextSelected,
                                ]}
                              >
                                {cat.label}
                              </Text>
                              {selectedCategory === cat.key && (
                                <Icon name="check" size={18} color={colors.primary} />
                              )}
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      </View>
                    )}
                  </>
                )}
              </>
            )}

            {/* Tag picker */}
            {kind === 'tag' && (
              <>
                <Text style={styles.label}>Tag</Text>
                {isEditing ? (
                  <View style={styles.disabledInput}>
                    <Text style={styles.disabledInputText}>{selectedTag}</Text>
                  </View>
                ) : (
                  <>
                    <TextInput
                      style={styles.input}
                      value={tagInput}
                      onChangeText={(text) => {
                        setTagInput(text);
                        setSelectedTag(text);
                      }}
                      onFocus={() => setShowTagPicker(true)}
                      placeholder="Saisir ou choisir un tag"
                      placeholderTextColor={colors.textSecondary}
                      autoCapitalize="none"
                    />
                    {showTagPicker && availableTags.length > 0 && (
                      <View style={styles.dropdownList}>
                        <ScrollView nestedScrollEnabled showsVerticalScrollIndicator={true}>
                          {availableTags
                            .filter(
                              (t) =>
                                !tagInput ||
                                t.name.toLowerCase().includes(tagInput.toLowerCase()),
                            )
                            .map((tag) => (
                              <TouchableOpacity
                                key={tag.name}
                                style={[
                                  styles.dropdownItem,
                                  selectedTag === tag.name && styles.dropdownItemSelected,
                                ]}
                                onPress={() => {
                                  setSelectedTag(tag.name);
                                  setTagInput(tag.name);
                                  setShowTagPicker(false);
                                }}
                              >
                                <View style={styles.tagRow}>
                                  {tag.color && (
                                    <View
                                      style={[styles.tagDot, { backgroundColor: tag.color }]}
                                    />
                                  )}
                                  <Text
                                    style={[
                                      styles.dropdownItemText,
                                      selectedTag === tag.name && styles.dropdownItemTextSelected,
                                    ]}
                                  >
                                    {tag.name}
                                  </Text>
                                </View>
                                {selectedTag === tag.name && (
                                  <Icon name="check" size={18} color={colors.primary} />
                                )}
                              </TouchableOpacity>
                            ))}
                        </ScrollView>
                      </View>
                    )}
                  </>
                )}
              </>
            )}

            {/* Budget limit */}
            <Text style={styles.label}>Plafond mensuel (€)</Text>
            <TextInput
              style={styles.input}
              value={monthlyLimit}
              onChangeText={setMonthlyLimit}
              keyboardType="numeric"
              placeholder="Ex: 500"
              placeholderTextColor={colors.textSecondary}
            />

            {/* Actions */}
            <View style={styles.actions}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={handleClose}
              >
                <Text style={styles.cancelButtonText}>Annuler</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.button,
                  styles.submitButton,
                  isSubmitting && styles.submitButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>
                    {isEditing ? 'Modifier' : 'Créer'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    backgroundColor: colors.background,
    borderRadius: 16,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 440,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
  },
  spentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.lg,
    gap: spacing.xs,
  },
  spentInfoText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  kindToggle: {
    flexDirection: 'row',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    padding: 4,
    marginBottom: spacing.md,
    gap: 4,
  },
  kindOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: 6,
  },
  kindOptionActive: {
    backgroundColor: colors.primary,
  },
  kindOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  kindOptionTextActive: {
    color: '#FFFFFF',
    fontWeight: typography.fontWeight.semibold as '600',
  },
  input: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.text,
    marginBottom: spacing.md,
  },
  disabledInput: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
    opacity: 0.7,
  },
  disabledInputText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  pickerButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
  },
  pickerButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
  },
  pickerButtonPlaceholder: {
    color: colors.textSecondary,
  },
  // Item 9: high-contrast dropdown that doesn't blend into the modal background
  dropdownList: {
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.md,
    maxHeight: 220,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 12,
    zIndex: 9999,
  },
  dropdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    backgroundColor: colors.background,
  },
  dropdownItemSelected: {
    backgroundColor: colors.primary + '10',
  },
  dropdownItemText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
  },
  dropdownItemTextSelected: {
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold as '600',
  },
  tagRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  tagDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  submitButton: {
    backgroundColor: colors.primary,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
});
